import type { PendingFileTreeAction } from "@promptlayer/ui";
import { useCallback, useEffect, useState } from "react";

import { getItemName } from "../../utils/path-utils";

interface UsePendingTreeActionParams {
  getCreateError: (
    parentPath: string | null,
    itemType: "file" | "folder",
    value: string,
  ) => string | null;
  getRenameError: (path: string, itemType: "file" | "folder", value: string) => string | null;
  onCreateFile: (path: string) => void;
  onCreateFolder: (path: string) => void;
  onRenameNode: (path: string, type: "file" | "folder", value: string) => void;
  setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const usePendingTreeAction = ({
  getCreateError,
  getRenameError,
  onCreateFile,
  onCreateFolder,
  onRenameNode,
  setExpandedFolders,
}: UsePendingTreeActionParams) => {
  const [pendingAction, setPendingAction] = useState<PendingFileTreeAction | null>(null);
  const [activeDirectoryPath, setActiveDirectoryPath] = useState<string | null>(null);
  const [createFileParentPath, setCreateFileParentPath] = useState<string | null>(null);
  const [isCreateFileDialogOpen, setIsCreateFileDialogOpen] = useState(false);

  const handlePendingActionValueChange = useCallback((value: string) => {
    setPendingAction(previousAction => {
      if (!previousAction) {
        return previousAction;
      }

      return {
        ...previousAction,
        value,
        error: null,
      };
    });
  }, []);

  const handleCancelPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  const handleSubmitPendingAction = useCallback(() => {
    if (!pendingAction) {
      return;
    }

    if (pendingAction.mode === "create") {
      const error = getCreateError(
        pendingAction.parentPath,
        pendingAction.itemType,
        pendingAction.value,
      );

      if (error) {
        setPendingAction(previousAction =>
          previousAction ? { ...previousAction, error } : previousAction,
        );
        return;
      }

      const targetPath = pendingAction.parentPath
        ? `${pendingAction.parentPath}/${pendingAction.value.trim()}`
        : pendingAction.value.trim();

      if (pendingAction.itemType === "file") {
        onCreateFile(targetPath);
      } else {
        onCreateFolder(targetPath);
        setExpandedFolders(previousFolders => new Set(previousFolders).add(targetPath));
      }

      setPendingAction(null);
      return;
    }

    const error = getRenameError(pendingAction.path, pendingAction.itemType, pendingAction.value);
    if (error) {
      setPendingAction(previousAction =>
        previousAction ? { ...previousAction, error } : previousAction,
      );
      return;
    }

    onRenameNode(pendingAction.path, pendingAction.itemType, pendingAction.value.trim());

    if (pendingAction.itemType === "folder") {
      const parentPath = pendingAction.path.split("/").slice(0, -1).join("/");
      const nextPath = pendingAction.value.trim()
        ? `${parentPath ? `${parentPath}/` : ""}${pendingAction.value.trim()}`
        : pendingAction.path;

      setActiveDirectoryPath(previousPath =>
        previousPath === pendingAction.path ? nextPath : previousPath,
      );
    }

    setPendingAction(null);
  }, [
    getCreateError,
    getRenameError,
    onCreateFile,
    onCreateFolder,
    onRenameNode,
    pendingAction,
    setExpandedFolders,
  ]);

  const handleRequestCreateFile = useCallback(
    (parentPath: string | null) => {
      setActiveDirectoryPath(parentPath);
      if (parentPath) {
        setExpandedFolders(previousFolders => new Set(previousFolders).add(parentPath));
      }
      setCreateFileParentPath(parentPath);
      setIsCreateFileDialogOpen(true);
    },
    [setExpandedFolders],
  );

  const handleRequestCreateFolder = useCallback((parentPath: string | null) => {
    setActiveDirectoryPath(parentPath);
    setPendingAction({
      mode: "create",
      itemType: "folder",
      parentPath,
      value: "",
      error: null,
    });
  }, []);

  const handleRequestRename = useCallback((path: string, itemType: "file" | "folder") => {
    if (itemType === "folder") {
      setActiveDirectoryPath(path);
    }

    setPendingAction({
      mode: "rename",
      itemType,
      path,
      value: getItemName(path),
      error: null,
    });
  }, []);

  useEffect(() => {
    if (pendingAction?.mode === "create" && pendingAction.parentPath) {
      const { parentPath } = pendingAction;
      setExpandedFolders(previousFolders => new Set(previousFolders).add(parentPath));
    }
  }, [pendingAction, setExpandedFolders]);

  const handleCreateFileDialogClose = useCallback((open: boolean) => {
    setIsCreateFileDialogOpen(open);
    if (!open) {
      setCreateFileParentPath(null);
    }
  }, []);

  return {
    pendingAction,
    activeDirectoryPath,
    setActiveDirectoryPath,
    createFileParentPath,
    isCreateFileDialogOpen,
    handleCreateFileDialogClose,
    handlePendingActionValueChange,
    handleCancelPendingAction,
    handleSubmitPendingAction,
    handleRequestCreateFile,
    handleRequestCreateFolder,
    handleRequestRename,
  };
};
